import {
  FontAwesomeIcon,
  type FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import { cn } from 'cn';

export type FaIconSize = 2 | 3 | 3.5 | 5 | 6 | 7 | 8 | 12;

interface Props extends Omit<FontAwesomeIconProps, 'size'> {
  size: FaIconSize;
  className?: string;
}

// `size-N!` carries `!important` to beat FontAwesome's default
// `.svg-inline--fa { height: 1em }` and any ancestor `[&_svg]:size-N` selector
// (e.g. from `<Button>` base) that would otherwise win the cascade at equal or
// higher specificity. Inline `style={{ width, height }}` would also work but is
// forbidden project-wide (`react/forbid-dom-props` in `eslint.config.mjs`).
const sizeClass: Record<FaIconSize, string> = {
  2: 'size-2!',
  3: 'size-3!',
  3.5: 'size-3.5!',
  5: 'size-5!',
  6: 'size-6!',
  7: 'size-7!',
  8: 'size-8!',
  12: 'size-12!',
};

/**
 * FontAwesome icon with a guaranteed size that doesn't depend on the original
 * call site repeating `size-N!`. Consumer `className` is merged onto the icon
 * itself, so text color, bg color, padding, animations, opacity, and
 * data-attribute variants behave exactly as they did when the original code
 * passed `size-N!` directly.
 */
export function FaIcon({ size, className, ...props }: Props) {
  return (
    <FontAwesomeIcon {...props} className={cn(sizeClass[size], className)} />
  );
}
